import { Box, Paper, Typography, Chip, Skeleton, Avatar, IconButton, Tooltip } from "@mui/material";
import { Refresh, Circle, ArrowBack } from "@mui/icons-material";
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import theme from "../theme/theme";
import { useEffect, useState, useRef } from "react";
import { fetchAllLatestLocations } from "../api/AttendanceService";
import { onMessage } from 'firebase/messaging';
import { messaging } from '../firebase-config';
import { useNavigate } from "react-router-dom";

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createEmployeeIcon = (isActive, initial) => {
    const color = isActive ? '#4CAF50' : '#9E9E9E';
    const border = isActive ? '#388E3C' : '#757575';
    return L.divIcon({
        className: 'custom-marker',
        html: `<div style="
            background:${color};
            color:#fff;
            border-radius:50%;
            width:36px;
            height:36px;
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:14px;
            font-weight:bold;
            border:3px solid ${border};
            box-shadow:0 2px 8px rgba(0,0,0,0.3);
            text-transform:uppercase;
        ">${initial}</div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
        popupAnchor: [0, -18],
    });
};

// Auto-fit bounds component
const FitBounds = ({ locations }) => {
    const map = useMap();
    useEffect(() => {
        if (locations.length > 0) {
            setTimeout(() => {
                map.invalidateSize();
                const bounds = L.latLngBounds(locations.map(loc => [parseFloat(loc.latitude), parseFloat(loc.longitude)]));
                map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
            }, 100);
        }
    }, [locations, map]);
    return null;
};

const isWithin10Min = (dateStr) => {
    if (!dateStr) return false;
    const now = new Date();
    const then = new Date(dateStr);
    return (now - then) < 10 * 60 * 1000;
};

const getTimeAgo = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ${diffMin % 60}m ago`;
    return `${Math.floor(diffHr / 24)}d ago`;
};

const getInitial = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0][0];
};

const LiveTrackingPage = () => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [highlightedId, setHighlightedId] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const logRefs = useRef({});
    const markerRefs = useRef({});
    const navigate = useNavigate();

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await fetchAllLatestLocations();
            setLocations(response.data?.locations || []);
            setLastRefresh(new Date());
        } catch (error) {
            setLocations([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);


    // Listen for FCM events to auto-refresh
    useEffect(() => {
        const unsubscribe = onMessage(messaging, (payload) => {
            const event = payload.data?.event;
            if (event === 'location_ping' || event === 'attendance_update') {
                fetchData();
            }
        });
        return () => unsubscribe();
    }, []);

    const validLocations = locations.filter(loc => loc.latitude && loc.longitude);
    const activeCount = validLocations.filter(loc => isWithin10Min(loc.created_at)).length;
    const inactiveCount = validLocations.length - activeCount;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/employees')} size="small">
                        <ArrowBack />
                    </IconButton>
                    <Box component="h1" sx={{ m: 0 }}>Live Tracking</Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Circle sx={{ fontSize: 14, color: '#4CAF50' }} />
                            <Typography variant="body2">Active ({activeCount})</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Circle sx={{ fontSize: 14, color: '#9E9E9E' }} />
                            <Typography variant="body2">Inactive ({inactiveCount})</Typography>
                        </Box>
                    </Box>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchData} size="small" sx={{ backgroundColor: 'rgba(0,0,0,0.04)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 140px)' }}>
                {/* Map */}
                <Paper sx={{ flex: '0 0 65%', borderRadius: theme.paper.borderRadius, backgroundColor: theme.paper.backgroundColor, overflow: 'hidden' }}>
                    {loading ? (
                        <Skeleton variant="rounded" height="100%" sx={{ borderRadius: 0 }} />
                    ) : validLocations.length > 0 ? (
                        <MapContainer
                            key={`live-map-${validLocations.length}`}
                            center={[parseFloat(validLocations[0].latitude), parseFloat(validLocations[0].longitude)]}
                            zoom={12}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={true}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <FitBounds locations={validLocations} />
                            {validLocations.map((loc) => {
                                const active = isWithin10Min(loc.created_at);
                                const empName = loc.employee?.name || 'Unknown';
                                const initial = getInitial(empName);
                                const empId = loc.employee_id;
                                return (
                                    <Marker
                                        key={empId}
                                        position={[parseFloat(loc.latitude), parseFloat(loc.longitude)]}
                                        icon={createEmployeeIcon(active, initial)}
                                        ref={(ref) => { if (ref) markerRefs.current[empId] = ref; }}
                                        eventHandlers={{
                                            click: () => {
                                                setHighlightedId(empId);
                                                if (logRefs.current[empId]) {
                                                    logRefs.current[empId].scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                }
                                            }
                                        }}
                                    >
                                        <Popup>
                                            <div style={{ minWidth: 160 }}>
                                                <strong>{empName}</strong><br />
                                                {loc.employee?.designation && <><span style={{ fontSize: 12, color: '#666' }}>{loc.employee.designation}</span><br /></>}
                                                <span style={{ fontSize: 12 }}>Last seen: {getTimeAgo(loc.created_at)}</span><br />
                                                <span style={{ fontSize: 12 }}>
                                                    {Number(loc.latitude).toFixed(6)}, {Number(loc.longitude).toFixed(6)}
                                                </span><br />
                                                <a
                                                    href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ fontSize: 12 }}
                                                >
                                                    Open in Google Maps
                                                </a>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}
                        </MapContainer>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">No location data available for today</Typography>
                        </Box>
                    )}
                </Paper>

                {/* Employee List Panel */}
                <Paper sx={{
                    flex: '0 0 35%', borderRadius: theme.paper.borderRadius, backgroundColor: theme.paper.backgroundColor,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" fontWeight="bold" color={theme.palette.primary.main}>
                            Employees ({validLocations.length})
                        </Typography>
                        {lastRefresh && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated: {lastRefresh.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{
                        flex: 1, overflowY: 'auto', p: 1.5,
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: 3 },
                    }}>
                        {loading ? (
                            [...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={70} sx={{ borderRadius: 2, mb: 1 }} />)
                        ) : validLocations.length > 0 ? (
                            [...validLocations]
                                .sort((a, b) => {
                                    // Active first, then by name
                                    const aActive = isWithin10Min(a.created_at);
                                    const bActive = isWithin10Min(b.created_at);
                                    if (aActive !== bActive) return bActive - aActive;
                                    return (a.employee?.name || '').localeCompare(b.employee?.name || '');
                                })
                                .map((loc) => {
                                    const empId = loc.employee_id;
                                    const empName = loc.employee?.name || 'Unknown';
                                    const active = isWithin10Min(loc.created_at);
                                    const isHighlighted = highlightedId === empId;
                                    const color = active ? '#4CAF50' : '#9E9E9E';

                                    return (
                                        <Box
                                            key={empId}
                                            ref={(el) => { logRefs.current[empId] = el; }}
                                            onClick={() => {
                                                setHighlightedId(empId);
                                                // Fly to marker on map
                                                if (markerRefs.current[empId]) {
                                                    const marker = markerRefs.current[empId];
                                                    const map = marker._map;
                                                    if (map) {
                                                        map.flyTo([parseFloat(loc.latitude), parseFloat(loc.longitude)], 16, { duration: 0.8 });
                                                    }
                                                    marker.openPopup();
                                                }
                                            }}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1,
                                                borderRadius: 2, cursor: 'pointer', transition: 'all 0.2s ease',
                                                border: '1px solid',
                                                borderColor: isHighlighted ? color : 'divider',
                                                backgroundColor: isHighlighted ? `${color}12` : 'transparent',
                                                boxShadow: isHighlighted ? `0 0 0 1px ${color}40` : 'none',
                                                '&:hover': { borderColor: color, backgroundColor: `${color}08` },
                                            }}
                                        >
                                            <Avatar sx={{
                                                width: 40, height: 40,
                                                backgroundColor: active ? '#4CAF50' : '#BDBDBD',
                                                fontSize: 16, fontWeight: 'bold',
                                            }}>
                                                {getInitial(empName)}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="body2" fontWeight="600" noWrap sx={{ fontSize: 13 }}>
                                                        {empName}
                                                    </Typography>
                                                    <Chip
                                                        icon={<Circle sx={{ fontSize: '10px !important' }} />}
                                                        label={active ? 'Active' : 'Inactive'}
                                                        size="small"
                                                        sx={{
                                                            height: 22, fontSize: 11, fontWeight: 600,
                                                            backgroundColor: `${color}18`, color: color,
                                                            '& .MuiChip-icon': { color: color },
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                    {loc.employee?.designation || '—'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                                    {getTimeAgo(loc.created_at)} • {Number(loc.latitude).toFixed(4)}, {Number(loc.longitude).toFixed(4)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4 }}>
                                <Typography variant="body2" color="text.secondary">No employee locations today</Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default LiveTrackingPage;
